import clsx from "clsx";
import { ImgHTMLAttributes, useEffect, useRef, useState } from "react";

interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  priority?: boolean;
  quality?: number;
  fill?: boolean;
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  onLoad?: () => void;
  onError?: () => void;
  sizes?: string;
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  objectPosition?: string;
}

export function Image({
  src,
  alt,
  width,
  height,
  priority = false,
  quality,
  fill = false,
  placeholder,
  blurDataURL,
  onLoad,
  onError,
  sizes,
  objectFit = "cover",
  objectPosition = "center",
  className,
  style,
  ...rest
}: ImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (priority && imgRef.current) {
      const img = new window.Image();
      img.src = src;
      img.onload = () => setIsLoaded(true);
    }
  }, [src, priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    onError?.();
  };

  const imgStyle = {
    ...style,
    ...(fill && {
      position: "absolute" as const,
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
      objectFit,
      objectPosition,
    }),
    ...(!fill && {
      width: typeof width === "number" ? `${width}px` : width,
      height: typeof height === "number" ? `${height}px` : height,
    }),
  };

  const shouldShowPlaceholder = placeholder === "blur" && blurDataURL && !isLoaded;

  return (
    <>
      {shouldShowPlaceholder && (
        <img
          src={blurDataURL}
          alt=""
          aria-hidden="true"
          className={clsx(className, "absolute inset-0 h-full w-full")}
          style={{
            ...imgStyle,
            filter: "blur(20px)",
            transform: "scale(1.05)",
          }}
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={clsx(
          className,
          shouldShowPlaceholder && "opacity-0 transition-opacity duration-500",
          isLoaded && "opacity-100",
        )}
        style={imgStyle}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        sizes={sizes}
        {...rest}
      />
    </>
  );
}

// Helper component for optimized static images
export function StaticImage({ src, alt, ...props }: Omit<ImageProps, "src"> & { src: string }) {
  // For static images in public folder, just use the path directly
  const imageSrc = src.startsWith("/") ? src : `/${src}`;

  return <Image src={imageSrc} alt={alt} {...props} />;
}
